import { createIcon } from "./icons";

export type SearchBoxPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

/**
 * Entrée recherchable générique — `ui/SearchBox` ne connaît ni `Shape` ni `Shape.entity` (frontière
 * core/adapter, voir CLAUDE.md) : c'est `createTokaMap.ts` qui construit cette liste à partir du
 * `ShapeStore` et de `TokaMapConfig.search.getSearchItem` (voir son commentaire), en dédupliquant
 * plusieurs formes vers UNE entrée quand elles partagent le même `SearchItem.key` (ex. plusieurs
 * formes découpant une même entité métier) — `shapeIds` porte alors tous les `Shape.id` concernés,
 * pour permettre de tous les surligner au clic plutôt qu'un seul.
 */
/**
 * Entrée de recherche qui ne désigne aucune forme du document affiché — typiquement un résultat
 * situé sur un AUTRE document (voir `SearchBoxEntry.external`). `payload` n'est jamais interprété
 * ici : `SearchBox` ne sait pas ce qu'est un "autre document", il ne fait que rendre l'entrée au
 * consommateur au clic.
 */
export interface SearchBoxExternalRef {
  payload: unknown;
}

export interface SearchBoxEntry {
  /** Vide pour une entrée `external` : il n'y a aucune forme chargée à surligner. */
  shapeIds: string[];
  title: string;
  subTitle?: string;
  image?: string;
  /** Texte additionnel comparé à la saisie AU MÊME TITRE que `title`, sans jamais être affiché —
   * ex. un champ d'adresse/emplacement qui doit être trouvable sans apparaître dans le résultat. */
  searchText?: string;
  /**
   * Présent : l'entrée ne vise pas le document affiché (voir `SearchBoxExternalRef`). Filtrée et
   * rendue exactement comme les autres — seul le traitement du clic diffère, et il appartient à
   * `createTokaMap`, pas à ce composant.
   */
  external?: SearchBoxExternalRef;
  categoryId?: string;
}

export interface SearchBoxCategory {
  id: string;
  label: string;
  color?: string;
}

/**
 * Bouton posé à droite du champ, après la loupe. Volontairement générique : `SearchBox` ne sait pas
 * ce qu'une action déclenche (frontière core/consommateur, voir CLAUDE.md).
 */
export interface SearchBoxAction {
  id: string;
  /** Infobulle et libellé d'accessibilité. */
  label: string;
  icon: () => SVGSVGElement;
  onClick: () => void;
  /** `"accent"` : fond teinté aux couleurs d'accent — pour l'action qui ouvre un mode, par
   * opposition à la loupe qui ne fait que valider la saisie. Par défaut : sans fond. */
  variant?: "plain" | "accent";
}

export interface SearchBoxOptions {
  /**
   * Reconstruit la liste des entrées recherchables — appelée à chaque frappe (pas mise en cache),
   * pour rester toujours cohérente avec l'état courant du `ShapeStore` sans avoir à s'abonner à ses
   * changements.
   */
  getEntries: () => SearchBoxEntry[];
  /** Coin de l'écran où placer le composant. Par défaut : "top-left". */
  position?: SearchBoxPosition;
  /** Fusionné par-dessus le style par défaut — même convention que `ResetViewButtonOptions.style`. */
  style?: Partial<CSSStyleDeclaration>;
  /** Texte affiché dans le champ vide. Par défaut : "Rechercher…". */
  placeholder?: string;
  /** Voir `SearchBoxAction` — rendues à droite du champ, dans l'ordre donné. */
  actions?: SearchBoxAction[];
  getCategories?: () => SearchBoxCategory[];
  onLayoutChange?: () => void;
}

/** Nombre maximum de résultats affichés — au-delà, le reste du texte tapé filtre encore la liste
 * (rien n'est perdu), seul l'affichage est borné pour ne pas construire des centaines de lignes DOM
 * à chaque frappe. */
const MAX_RESULTS = 50;

/** Même seuil que le passage de `EntityPanel` en bottom sheet. */
const COMPACT_MAX_WIDTH_PX = 700;

// Mêmes offsets "bord d'écran" que `ResetViewButton`/`GeolocationButton` — quand les deux widgets
// partagent le même coin par défaut, `createTokaMap.ts` décale `ResetViewButton` (jamais `SearchBox`,
// qui garde toujours cette position bord) pour que la recherche apparaisse au-dessus, voir son
// commentaire sur `SEARCH_STACK_OFFSET_PX`.
const POSITION_OFFSETS: Record<SearchBoxPosition, Partial<CSSStyleDeclaration>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-right": { top: "10px", right: "10px" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-right": { bottom: "10px", right: "10px" },
};

/** Insensible à la casse ET aux accents (ex. "école" trouvé en tapant "ecole") — décompose les
 * diacritiques (NFD) puis les retire, plutôt qu'une simple `toLowerCase()`. */
function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Champ de recherche flottant (BESOINS.md, voir img.png à la racine du repo pour la maquette) :
 * filtre localement sur `SearchBoxEntry.title` une liste reconstruite à la demande (voir
 * `getEntries`), affiche une liste déroulante (image + titre + sous-titre), et appelle `onSelect`
 * au clic sur un résultat — `createTokaMap.ts` s'en sert pour surligner la forme correspondante
 * puis relayer l'évènement à `TokaMapConfig.search.onSearch` (voir son commentaire). Appelle
 * `onClear` quand la recherche redevient vide (bouton "x", ou dernier caractère supprimé au
 * clavier) — `createTokaMap.ts` s'en sert pour effacer le surlignage posé par `onSelect`.
 */
export class SearchBox {
  private container: HTMLDivElement;
  private input: HTMLInputElement;
  private clearButton: HTMLButtonElement;
  private submitButton: HTMLButtonElement;
  private resultsEl: HTMLDivElement;
  private categoriesEl: HTMLDivElement;
  private categoriesScroller: HTMLDivElement;
  private scrollPrevButton: HTMLButtonElement;
  private scrollNextButton: HTMLButtonElement;
  private categoriesResizeObserver: ResizeObserver;
  private inputWrapper: HTMLDivElement;
  private activeCategoryId: string | null = null;
  private selectedTitle: string | null = null;
  private openButton: HTMLButtonElement;
  private mapContainer: HTMLElement;
  private mapResizeObserver: ResizeObserver;
  private compact = false;
  private expanded = false;
  private hiddenByHost = false;
  private activeIndex = -1;
  private currentEntries: SearchBoxEntry[] = [];
  private actionButtons = new Map<string, HTMLButtonElement>();

  constructor(
    container: HTMLElement,
    private options: SearchBoxOptions,
    private onSelect: (entry: SearchBoxEntry) => void,
    private onClear?: () => void,
  ) {
    this.container = document.createElement("div");
    this.container.className = "toka-search-box";
    Object.assign(
      this.container.style,
      POSITION_OFFSETS[options.position ?? "top-left"],
      options.style,
    );

    const inputWrapper = document.createElement("div");
    inputWrapper.className = "toka-search-box__input-wrapper";
    this.inputWrapper = inputWrapper;

    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.className = "toka-search-box__input";
    this.input.placeholder = options.placeholder ?? "Rechercher…";
    this.input.setAttribute("aria-label", options.placeholder ?? "Rechercher");
    this.input.addEventListener("input", this.handleInput);
    this.input.addEventListener("keydown", this.handleKeyDown);
    this.input.addEventListener("focus", this.handleInput);
    inputWrapper.appendChild(this.input);

    this.clearButton = document.createElement("button");
    this.clearButton.type = "button";
    this.clearButton.className = "toka-search-box__clear";
    this.clearButton.textContent = "✕";
    this.clearButton.setAttribute("aria-label", "Effacer la recherche");
    this.clearButton.addEventListener("click", () => {
      this.input.value = "";
      this.selectedTitle = null;
      this.input.focus();
      this.showMatches();
      this.onClear?.();
    });
    inputWrapper.appendChild(this.clearButton);
    this.updateClearButtonVisibility();

    const actionsEl = document.createElement("div");
    actionsEl.className = "toka-search-box__actions";

    const submitLabel = "Rechercher";
    this.submitButton = document.createElement("button");
    this.submitButton.type = "button";
    this.submitButton.className = "toka-search-box__action toka-search-box__submit";
    this.submitButton.appendChild(createSearchIcon());
    this.submitButton.setAttribute("aria-label", submitLabel);
    this.submitButton.title = submitLabel;
    this.submitButton.addEventListener("click", this.handleSubmit);
    actionsEl.appendChild(this.submitButton);

    for (const action of options.actions ?? []) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "toka-search-box__action";
      if (action.variant === "accent") button.classList.add("toka-search-box__action--accent");
      button.appendChild(action.icon());
      button.setAttribute("aria-label", action.label);
      button.title = action.label;
      button.addEventListener("click", action.onClick);
      actionsEl.appendChild(button);
      this.actionButtons.set(action.id, button);
    }

    inputWrapper.appendChild(actionsEl);

    this.container.appendChild(inputWrapper);

    this.categoriesEl = document.createElement("div");
    this.categoriesEl.className = "toka-search-box__categories";
    this.categoriesEl.style.display = "none";
    this.categoriesScroller = document.createElement("div");
    this.categoriesScroller.className = "toka-search-box__categories-scroller";
    this.categoriesScroller.addEventListener("wheel", this.handleCategoriesWheel, { passive: false });
    this.categoriesScroller.addEventListener("scroll", () => this.updateScrollButtons());
    this.scrollPrevButton = createScrollButton("prev", () => this.scrollCategories(-1));
    this.scrollNextButton = createScrollButton("next", () => this.scrollCategories(1));
    this.categoriesEl.append(this.scrollPrevButton, this.categoriesScroller, this.scrollNextButton);
    this.categoriesResizeObserver = new ResizeObserver(() => this.updateScrollButtons());
    this.categoriesResizeObserver.observe(this.categoriesScroller);
    this.container.appendChild(this.categoriesEl);

    this.resultsEl = document.createElement("div");
    this.resultsEl.className = "toka-search-box__results";
    this.container.appendChild(this.resultsEl);

    container.appendChild(this.container);

    const openLabel = options.placeholder ?? "Rechercher";
    this.openButton = document.createElement("button");
    this.openButton.type = "button";
    this.openButton.className = "toka-search-box-open";
    this.openButton.appendChild(createSearchIcon());
    this.openButton.setAttribute("aria-label", openLabel);
    this.openButton.title = openLabel;
    this.openButton.addEventListener("click", () => this.setExpanded(true));
    container.appendChild(this.openButton);

    this.mapContainer = container;
    this.mapResizeObserver = new ResizeObserver(() => this.updateCompact());
    this.mapResizeObserver.observe(container);
    this.compact = this.isCompactWidth();
    this.applyVisibility();

    document.addEventListener("pointerdown", this.handleOutsideClick);
  }

  private handleInput = (event: Event): void => {
    if (event.type === "input") this.selectedTitle = null;
    this.updateClearButtonVisibility();
    if (!this.input.value.trim()) this.onClear?.();
    this.showMatches();
  };

  private showMatches(): void {
    const query = normalize(this.input.value.trim());
    const categoryId = this.activeCategoryId;
    if (!query && !categoryId) {
      this.renderResults([]);
      return;
    }
    const matches = this.options
      .getEntries()
      .filter((entry) => !categoryId || entry.categoryId === categoryId)
      .filter((entry) => !query || normalize(`${entry.title} ${entry.searchText ?? ""}`).includes(query));
    this.renderResults(
      categoryId ? matches.sort((a, b) => a.title.localeCompare(b.title)) : matches.slice(0, MAX_RESULTS),
    );
  }

  refreshCategories(): void {
    const presentIds = new Set(this.options.getEntries().map((entry) => entry.categoryId));
    const categories = (this.options.getCategories?.() ?? []).filter((category) => presentIds.has(category.id));
    if (this.activeCategoryId && !categories.some((category) => category.id === this.activeCategoryId)) {
      this.closeResults();
    }

    this.categoriesScroller.replaceChildren(
      ...categories.map((category) => {
        const tag = document.createElement("button");
        tag.type = "button";
        tag.className = "toka-search-box__category";
        tag.dataset.categoryId = category.id;
        if (category.color) {
          const dot = document.createElement("span");
          dot.className = "toka-search-box__category-dot";
          dot.style.background = category.color;
          tag.appendChild(dot);
        }
        tag.appendChild(document.createTextNode(category.label));
        tag.addEventListener("click", () =>
          this.setActiveCategory(this.activeCategoryId === category.id ? null : category.id),
        );
        return tag;
      }),
    );
    this.renderActiveCategory();

    const display = categories.length ? "" : "none";
    if (this.categoriesEl.style.display !== display) {
      this.categoriesEl.style.display = display;
      this.options.onLayoutChange?.();
    }
    this.updateScrollButtons();
  }

  private scrollCategories(direction: 1 | -1): void {
    const distance = Math.max(this.categoriesScroller.clientWidth * 0.7, 60);
    this.categoriesScroller.scrollBy({ left: direction * distance, behavior: "smooth" });
  }

  private updateScrollButtons(): void {
    const { scrollLeft, scrollWidth, clientWidth } = this.categoriesScroller;
    const canPrev = scrollLeft > 1;
    const canNext = scrollLeft + clientWidth < scrollWidth - 1;
    this.scrollPrevButton.hidden = !canPrev;
    this.scrollNextButton.hidden = !canNext;
    this.scrollPrevButton.tabIndex = canPrev ? 0 : -1;
    this.scrollNextButton.tabIndex = canNext ? 0 : -1;
    this.categoriesEl.classList.toggle("toka-search-box__categories--can-prev", canPrev);
    this.categoriesEl.classList.toggle("toka-search-box__categories--can-next", canNext);
  }

  private setActiveCategory(categoryId: string | null): void {
    if (categoryId && this.selectedTitle !== null && this.input.value === this.selectedTitle) {
      this.input.value = "";
      this.selectedTitle = null;
      this.updateClearButtonVisibility();
      this.onClear?.();
    }
    this.activeCategoryId = categoryId;
    this.renderActiveCategory();
    this.showMatches();
  }

  private renderActiveCategory(): void {
    for (const tag of this.categoriesScroller.querySelectorAll<HTMLButtonElement>(".toka-search-box__category")) {
      const active = tag.dataset.categoryId === this.activeCategoryId;
      tag.classList.toggle("toka-search-box__category--active", active);
      tag.setAttribute("aria-pressed", String(active));
    }
  }

  private closeResults(): void {
    this.activeCategoryId = null;
    this.renderActiveCategory();
    this.renderResults([]);
  }

  private handleCategoriesWheel = (event: WheelEvent): void => {
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    if (this.categoriesScroller.scrollWidth <= this.categoriesScroller.clientWidth) return;
    event.preventDefault();
    this.categoriesScroller.scrollLeft += event.deltaY;
  };

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      this.closeResults();
      this.setExpanded(false);
      return;
    }
    if (!this.currentEntries.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.setActiveIndex((this.activeIndex + 1) % this.currentEntries.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.setActiveIndex((this.activeIndex - 1 + this.currentEntries.length) % this.currentEntries.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      this.handleSubmit();
    }
  };

  /** Partagé par la touche Entrée et le clic sur la loupe, pour que les deux fassent la même chose.
   * Sans résultat actif, rend la main au champ plutôt que de rester inerte. */
  private handleSubmit = (): void => {
    if (this.activeIndex >= 0 && this.currentEntries[this.activeIndex]) {
      this.selectEntry(this.currentEntries[this.activeIndex]!);
      return;
    }
    this.input.focus();
  };

  private handleOutsideClick = (event: PointerEvent): void => {
    if (event.target instanceof Node && this.container.contains(event.target)) return;
    this.closeResults();
    this.setExpanded(false);
  };

  private isCompactWidth(): boolean {
    return this.mapContainer.clientWidth > 0 && this.mapContainer.clientWidth <= COMPACT_MAX_WIDTH_PX;
  }

  private updateCompact(): void {
    const compact = this.isCompactWidth();
    if (compact === this.compact) return;
    this.compact = compact;
    this.expanded = false;
    this.applyVisibility();
  }

  private setExpanded(expanded: boolean): void {
    if (!this.compact || expanded === this.expanded) return;
    this.expanded = expanded;
    this.applyVisibility();
    if (expanded) this.input.focus();
  }

  private applyVisibility(): void {
    const panelVisible = !this.hiddenByHost && (!this.compact || this.expanded);
    this.container.style.display = panelVisible ? "" : "none";
    this.container.classList.toggle("toka-search-box--compact", this.compact);
    this.openButton.style.display = !this.hiddenByHost && this.compact && !this.expanded ? "" : "none";
    this.options.onLayoutChange?.();
  }

  private updateClearButtonVisibility(): void {
    this.clearButton.style.display = this.input.value ? "" : "none";
  }

  private setActiveIndex(index: number): void {
    this.activeIndex = index;
    const rows = this.resultsEl.querySelectorAll<HTMLButtonElement>(".toka-search-box__result");
    rows.forEach((row, rowIndex) => {
      row.classList.toggle("toka-search-box__result--active", rowIndex === index);
    });
    rows[index]?.scrollIntoView({ block: "nearest" });
  }

  private selectEntry(entry: SearchBoxEntry): void {
    this.input.value = entry.title;
    this.selectedTitle = entry.title;
    this.updateClearButtonVisibility();
    this.closeResults();
    this.setExpanded(false);
    this.onSelect(entry);
  }

  private renderResults(entries: SearchBoxEntry[]): void {
    this.currentEntries = entries;
    this.activeIndex = -1;
    this.resultsEl.innerHTML = "";

    if (!entries.length) {
      this.resultsEl.style.display = "none";
      return;
    }
    // "flex" explicitement (pas "") : `.toka-search-box__results` déclare `display: none` par
    // défaut dans la feuille de style (état masqué) — vider le style inline retomberait sur cette
    // valeur au lieu de l'afficher.
    this.resultsEl.style.display = "flex";

    for (const entry of entries) {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "toka-search-box__result";

      if (entry.image) {
        const image = document.createElement("img");
        image.className = "toka-search-box__result-image";
        image.src = entry.image;
        image.alt = "";
        row.appendChild(image);
      } else {
        const placeholder = document.createElement("span");
        placeholder.className = "toka-search-box__result-image toka-search-box__result-image--placeholder";
        row.appendChild(placeholder);
      }

      const text = document.createElement("span");
      text.className = "toka-search-box__result-text";

      const title = document.createElement("span");
      title.className = "toka-search-box__result-title";
      title.textContent = entry.title;
      text.appendChild(title);

      if (entry.subTitle) {
        const subTitle = document.createElement("span");
        subTitle.className = "toka-search-box__result-subtitle";
        subTitle.textContent = entry.subTitle;
        text.appendChild(subTitle);
      }

      row.appendChild(text);
      // `mousedown` (pas `click`) : se déclenche avant le `blur`/`focus`-out de l'input, qui
      // fermerait sinon la liste (via `handleOutsideClick`) avant que le clic n'atteigne cette ligne.
      row.addEventListener("mousedown", (event) => {
        event.preventDefault();
        this.selectEntry(entry);
      });
      this.resultsEl.appendChild(row);
    }

    this.setActiveIndex(0);
  }

  /** Masque le widget sans le détruire (`destroy()` le retire définitivement) — voir
   * `createTokaMap.ts`'s `"map-view:edit-started"`/`"map-view:edit-ended"` : la recherche de forme
   * n'a pas sa place pendant que l'utilisateur recadre la vue par défaut, elle réapparaît telle
   * quelle (recherche en cours conservée) une fois le mode terminé. */
  hide(): void {
    this.hiddenByHost = true;
    this.applyVisibility();
  }

  show(): void {
    this.hiddenByHost = false;
    this.applyVisibility();
  }

  /** Décale le widget vers l'intérieur de la carte, sans toucher à sa position configurée. Sans
   * effet sur une recherche placée à droite. */
  setLeftShift(px: number): void {
    const isLeft = (this.options.position ?? "top-left").endsWith("left");
    this.container.style.translate = isLeft && px > 0 ? `${px}px 0` : "";
  }

  /** Hauteur hors liste de résultats, qui se déplie par-dessus la carte. */
  heightPx(): number {
    if (this.container.style.display === "none") return 0;
    const header = this.categoriesEl.style.display === "none" ? this.inputWrapper : this.categoriesEl;
    return header.offsetTop + header.offsetHeight;
  }

  widthPx(): number {
    return parseFloat(getComputedStyle(this.container).width) || 0;
  }

  /** Masque une action sans la retirer. Sans effet si l'id est inconnu. */
  setActionVisible(id: string, visible: boolean): void {
    const button = this.actionButtons.get(id);
    if (button) button.style.display = visible ? "" : "none";
  }

  destroy(): void {
    document.removeEventListener("pointerdown", this.handleOutsideClick);
    this.categoriesResizeObserver.disconnect();
    this.mapResizeObserver.disconnect();
    this.openButton.remove();
    this.container.remove();
  }
}

function createScrollButton(direction: "prev" | "next", onClick: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `toka-search-box__categories-scroll toka-search-box__categories-scroll--${direction}`;
  const label = direction === "prev" ? "Catégories précédentes" : "Catégories suivantes";
  button.setAttribute("aria-label", label);
  button.title = label;
  button.appendChild(createIcon("chevron-down"));
  button.hidden = true;
  button.addEventListener("click", onClick);
  return button;
}

function createSearchIcon(): SVGSVGElement {
  const SVG_NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const circle = document.createElementNS(SVG_NS, "circle");
  circle.setAttribute("cx", "11");
  circle.setAttribute("cy", "11");
  circle.setAttribute("r", "7");
  const line = document.createElementNS(SVG_NS, "path");
  line.setAttribute("d", "M21 21l-4.3-4.3");
  svg.appendChild(circle);
  svg.appendChild(line);
  return svg;
}
